import _ from "lodash";
import React from "react";
import { getUserNotificationCountFromCache } from "modules/CommonGraphqlHelpers";
import {
  getCategoryTabsForNotification,
  getNotificationCategory,
} from "modules/Services";
import { notificationTypes } from "AppComponents/NotificationCenter/ListContainer/ListContainer";
import { isIEAssessment } from "Projects/modules/ProjectModules";
import { getRethumbUrl } from "Utils";
import { colors } from "@toddle-edu/ds-theme";
import {
  AttendanceOutlined,
  EnvelopeDotOutlined,
  PostOneColored,
  NoteColored,
  WorkbookColored,
  GeneralAssessmentColored,
  LinkColored,
  AudioColored,
  VideoColored,
  ImageSquareColored,
  HeartLikeColored,
  CommentMultipleColored,
  LearningExperienceColored,
  UnitPlanOutlined,
  MentionOutlined,
  ProgressReportOutlined,
  EnvelopePlainOutlined,
  ClipboardTickOutlined,
  AssessmentEvaluatedOutlined,
  AssessmentOutlined,
  WarningTriangleFilled,
  PolicyTickOutlined,
  ResourceOutlined,
  PlaceholderFileOutlined,
  ZipFileOutlined,
  LoudspeakerOutlined,
  WarningTriangleColored,
  TickCircleFilled,
  AddCircleFilled,
  CommentSquareLeftClassroomOutlined,
  LinkOutlined,
  ImageSquareOutlined,
  PdfOutlined,
  SheetsOutlined,
  SlidesOutlined,
  VideoOutlined,
  VolumeHighOutlined,
  GoalsOutlined,
  StudentOutlined,
  ClockAlarmOutlined,
  SubsectionListOutlined,
  NoteOutlined,
  HeadHeartOutlined,
  CalendarOutlined,
  QuickTaskOutlined,
} from "@toddle-edu/ds-icons";
import {
  IcnNotiAddedPost,
  SubmissionIcon,
  IcnNotiSubmissionReturned,
  IcnNotiEvaluationCompleted,
} from "SvgComponents";
import { getIsAssessmentTypeConfigurationEnabled } from "Utils/ACLStoreUtils";

const styles = {
  classroomMessageIconStyle: {
    color: colors.violet300,
  },
};

import { Acronym, Avatar } from "@toddle-edu/ds-web";
import { ALERT_TYPES } from "ClassRoom/modules/utils";
import { CLASS_MATERIALS_FILE_TYPES } from "ClassMaterials/modules/utils";

const classroomMessageIconData = {
  dsIcon: (
    <CommentSquareLeftClassroomOutlined
      style={styles.classroomMessageIconStyle}
      size={"x-small"}
    />
  ),
  bgColor: colors.violet950,
};

const commonProjectNotificationSubCategories = {
  PERSONAL: {
    studentLabel: "project:personal_project",
    dsIcon: <Acronym size={"x-small"} color={colors.teal300} name={"PP"} />,
    bgColor: colors.teal900,
    stackedIcons: true,
  },
  COMMUNITY: {
    studentLabel: "project:community_project",
    dsIcon: <Acronym size={"x-small"} color={colors.pink300} name={"CP"} />,
    bgColor: colors.pink900,
    stackedIcons: true,
  },
  SERVICE_AS_ACTION: {
    studentLabel: "project:service_as_action",
    dsIcon: <Acronym size={"x-small"} color={colors.violet300} name={"SAA"} />,
    bgColor: colors.violet900,
    stackedIcons: true,
  },
  DP_CAS: {
    studentLabel: "project:creativity_action_service",
    dsIcon: <Acronym size={"x-small"} color={colors.yellow300} name={"CAS"} />,
    bgColor: colors.yellow900,
    stackedIcons: true,
  },
  CP_SERVICE_LEARNING: {
    studentLabel: "project:service_learning",
    dsIcon: <Acronym size={"x-small"} color={colors.yellow300} name={"SL"} />,
    bgColor: colors.yellow900,
    stackedIcons: true,
  },
  COMMUNITY_ENGAGEMENT: {
    studentLabel: "project:community_engagement",
    dsIcon: <Acronym size={"x-small"} color={colors.yellow300} name={"CE"} />,
    bgColor: colors.yellow900,
    stackedIcons: true,
  },
  DP_EE: {
    studentLabel: "project:extended_essay",
    dsIcon: <Acronym size={"x-small"} color={colors.pink300} name={"EE"} />,
    bgColor: colors.pink900,
    stackedIcons: true,
  },
  DP_TOK_ESSAY: {
    studentLabel: "project:tok_essay_sentence_case",
    dsIcon: <Acronym size={"x-small"} color={colors.yellow300} name={"TOK"} />,
    bgColor: colors.yellow900,
    stackedIcons: true,
  },
  DP_TOK_EXHIBITION: {
    studentLabel: "project:tok_exhibition_sentence_case",
    dsIcon: <Acronym size={"x-small"} color={colors.yellow300} name={"TOK"} />,
    bgColor: colors.yellow900,
    stackedIcons: true,
  },
  INTERNAL_ASSESSMENT: {
    dsIcon: <Acronym size={"x-small"} color={colors.blue300} name={"IA"} />,
    bgColor: colors.blue900,
    stackedIcons: true,
  },
  EXTERNAL_ASSESSMENT: {
    dsIcon: <Acronym size={"x-small"} color={colors.teal300} name={"EA"} />,
    bgColor: colors.teal900,
    stackedIcons: true,
  },
  DP_G4_PROJECT: {
    studentLabel: "project:group_4_project",
    dsIcon: <Acronym size={"x-small"} color={colors.teal300} name={"G4"} />,
    bgColor: colors.teal900,
    stackedIcons: true,
  },
  CP_REFLECTIVE_PROJECT: {
    studentLabel: "project:reflective_project",
    dsIcon: <Acronym size={"x-small"} color={colors.pink300} name={"RP"} />,
    bgColor: colors.pink900,
    stackedIcons: true,
  },
  DP_COLLABORATIVE_SCIENCES_PROJECT: {
    studentLabel: "project:dp_collaborative_sciences_project",
    dsIcon: <Acronym size={"x-small"} color={colors.teal300} name={"CSP"} />,
    bgColor: colors.teal900,
    stackedIcons: true,
  },
};

const getCommonClassRoomNotificationSubCategories = useStackedIcon => {
  return {
    ASSIGNMENT_RESOURCE: {
      dsIcon: <AssessmentOutlined variant={"success"} size={"x-small"} />,
      bgColor: colors.decorativeSurfaceFiveL,
      stackedIcons: useStackedIcon,
    },
    ASSESSMENT: {
      dsIcon: <AssessmentOutlined variant={"success"} size={"x-small"} />,
      bgColor: colors.decorativeSurfaceFiveL,
      stackedIcons: useStackedIcon,
    },
    le: {
      dsIcon: <Acronym size={"x-small"} color={colors.violet300} name={"LE"} />,
      bgColor: colors.violet900,
      stackedIcons: useStackedIcon,
    },
    fmt: {
      dsIcon: <Acronym size={"x-small"} color={colors.yellow300} name={"FA"} />,
      bgColor: colors.yellow900,
      stackedIcons: useStackedIcon,
    },
    smt: {
      dsIcon: <Acronym size={"x-small"} color={colors.teal300} name={"SA"} />,
      bgColor: colors.teal900,
      stackedIcons: useStackedIcon,
    },
    qt: {
      dsIcon: <Acronym size={"x-small"} color={colors.blue300} name={"QT"} />,
      bgColor: colors.blue900,
      stackedIcons: useStackedIcon,
    },
    dp_pa: {
      dsIcon: <AssessmentOutlined variant={"success"} size={"x-small"} />,
      bgColor: colors.decorativeSurfaceFiveL,
      stackedIcons: useStackedIcon,
    },
    dp_ep: {
      dsIcon: <AssessmentOutlined variant={"success"} size={"x-small"} />,
      bgColor: colors.decorativeSurfaceFiveL,
      stackedIcons: useStackedIcon,
    },
    worksheet: {
      dsIcon: (
        <Acronym size={"xx-small"} color={colors.yellow300} name={"WS"} />
      ),
      bgColor: colors.yellow900,
      stackedIcons: useStackedIcon,
    },
    STUDENT_ASSIGNMENT_ALERT_MESSAGE: {
      dsIcon: <ClockAlarmOutlined variant="warning" />,
      bgColor: colors.yellow900,
      stackedIcons: useStackedIcon,
    },
    ai_tutor: {
      dsIcon: <Acronym size={"x-small"} color={colors.purple300} name={"AI"} />,
      bgColor: colors.purple900,
      stackedIcons: useStackedIcon,
    },
  };
};

const notificationTypesV2 = {
  // Attendance notifications
  ATTENDANCE_MARKED: {
    label: "notifications:notif_attendance_title",
    dsIcon: <AttendanceOutlined variant="warning" />,
    bgColor: colors.decorativeSurfaceThreeL,
  },
  CIRCULAR_ADDED: {
    label: "announcement:school_announcement",
    subCategories: {
      DEFAULT: {
        label: "announcement:school_announcement",
        svgIcon: (
          <LoudspeakerOutlined size="xx-small" color={colors.violet300} />
        ),
        bgColor: colors.violet900,
      },
      CLASS_ANNOUNCEMENT: {
        label: "announcement:class_announcement",
        svgIcon: <QuickTaskOutlined size="xx-small" color={colors.green300} />,
        bgColor: colors.green900,
      },
    },
  },
  CIRCULAR_REVIEW: {
    svgIcon: (
      <LoudspeakerOutlined
        size="xx-small"
        color={colors.pink300}
        variant="warning"
      />
    ),
    bgColor: colors.pink900,
  },
  ATTENDANCE_REMINDER: {
    label: "notifications:notif_attendance_title",
    dsIcon: <AttendanceOutlined size={"x-small"} variant="warning" />,
    secondaryIcon: {
      dsIcon: <WarningTriangleFilled size={"xxx-small"} variant="warning" />,
    },
    bgColor: colors.decorativeSurfaceThreeL,
  },
  ATTENDANCE_REMINDER_TO_HOMEROOM: {
    label: "notifications:notif_attendance_title",
    dsIcon: <AttendanceOutlined size={"x-small"} variant="warning" />,
    secondaryIcon: {
      dsIcon: <WarningTriangleFilled size={"xxx-small"} variant="warning" />,
    },
    bgColor: colors.decorativeSurfaceThreeL,
  },
  NEW_ATTENDANCE_EXCUSAL_RECEIVED: {
    label: "notifications:notif_attendance_title",
    familyLabel: "notifications:notif_attendance_title",
    dsIcon: <EnvelopeDotOutlined size={"x-small"} variant="warning" />,
    bgColor: colors.decorativeSurfaceThreeL,
  },
  NEW_ATTENDANCE_EXCUSAL_RECEIVED_TO_ADMIN: {
    label: "notifications:notif_attendance_title",
    dsIcon: <EnvelopeDotOutlined size={"x-small"} variant="warning" />,
    bgColor: colors.decorativeSurfaceThreeL,
  },
  NEW_ATTENDANCE_EXCUSAL_RECEIVED_TO_HOMEROOM: {
    label: "notifications:notif_attendance_title",
    dsIcon: <EnvelopeDotOutlined size={"x-small"} variant="warning" />,
    bgColor: colors.decorativeSurfaceThreeL,
  },
  NEW_ATTENDANCE_EXCUSAL_RECEIVED_TO_PARENT: {
    label: "notifications:notif_attendance_title",
    familyLabel: "notifications:notif_attendance_title",
    dsIcon: <EnvelopeDotOutlined size={"x-small"} variant="warning" />,
    bgColor: colors.decorativeSurfaceThreeL,
  },
  ATTENDANCE_MARKED_ON_EXCUSAL: {
    label: "notifications:notif_attendance_title",
    dsIcon: <AttendanceOutlined size={"x-small"} variant="warning" />,
    bgColor: colors.decorativeSurfaceThreeL,
  },
  ATTENDANCE_MARKED_ON_EXCUSAL_TO_HOMEROOM: {
    label: "notifications:notif_attendance_title",
    svgIcon: <AttendanceOutlined size={"x-small"} variant="warning" />,
    bgColor: colors.decorativeSurfaceThreeL,
  },
  ATTENDANCE_EXCUSAL_MESSAGE_RECEIVED: {
    label: "notifications:notif_attendance_title",
    svgIcon: <EnvelopeDotOutlined size={"x-small"} variant="warning" />,
    bgColor: colors.decorativeSurfaceThreeL,
    stackedIcons: true,
  },
  ATTENDANCE_EXCUSAL_MESSAGE_RECEIVED_TO_HOMEROOM: {
    label: "attendance:new_message_excusal",
    svgIcon: <EnvelopeDotOutlined size={"x-small"} variant="warning" />,
    bgColor: colors.decorativeSurfaceThreeL,
    stackedIcons: true,
  },

  // Portfolio notifications
  POST: {
    label: "common:tagged_post",
    bgColor: colors.decorativeSurfaceFourL,
    dsIcon: <PostOneColored size={"x-small"} />,
    stackedIcons: true,
    subCategories: {
      NOTE: {
        dsIcon: <NoteColored size={"x-small"} />,
        bgColor: colors.decorativeSurfaceThreeL,
      },
      WORKBOOK: {
        dsIcon: <WorkbookColored size={"x-small"} />,
        bgColor: colors.decorativeSurfaceThreeL,
      },
      FILE: {
        dsIcon: <GeneralAssessmentColored size={"x-small"} />,
        bgColor: colors.decorativeSurfaceThreeL,
      },
      LINK: {
        dsIcon: <LinkColored size={"x-small"} />,
        bgColor: colors.decorativeSurfaceFiveL,
      },
      AUDIO: {
        dsIcon: <AudioColored size={"x-small"} />,
        bgColor: colors.decorativeSurfaceTwoL,
      },
      VIDEO: {
        dsIcon: <VideoColored size={"x-small"} />,
        bgColor: colors.decorativeSurfaceFourL,
      },
      IMAGE: {
        dsIcon: <ImageSquareColored size={"x-small"} />,
        bgColor: colors.decorativeSurfaceFiveL,
      },
      POST: {
        stackedIcons: true,
        dsIcon: <PostOneColored size={"x-small"} />,
        bgColor: colors.decorativeSurfaceFourL,
      },
    },
  },
  POST_LIKE: {
    label: "common:likes",
    dsIcon: <HeartLikeColored size={"x-small"} />,
    bgColor: colors.decorativeSurfaceFourL,
    stackedIcons: true,
  },
  COMMENT: {
    label: "common:comment",
    dsIcon: <CommentMultipleColored size={"x-small"} />,
    bgColor: colors.decorativeSurfaceThreeL,
    stackedIcons: true,
  },
  COMMENT_ASSESSMENT: {
    label: "common:unit_plan",
    dsIcon: <LearningExperienceColored size={"x-small"} />,
    bgColor: colors.decorativeSurfaceTwoL,
    stackedIcons: true,
    subCategories: {
      DEFAULT: {
        dsIcon: <GeneralAssessmentColored size={"x-small"} />,
        bgColor: colors.decorativeSurfaceThreeL,
      },
      TAGGED: {
        dsIcon: <GeneralAssessmentColored size={"x-small"} />,
        bgColor: colors.decorativeSurfaceThreeL,
        secondaryIcon: {
          dsIcon: <MentionOutlined size={"xxx-small"} variant="on" />,
          bgColor: colors.surfaceOnError,
        },
      },
      AUDIO: {
        dsIcon: <GeneralAssessmentColored size={"x-small"} />,
        bgColor: colors.decorativeSurfaceThreeL,
      },
      AUDIO_TAGGED: {
        dsIcon: <GeneralAssessmentColored size={"x-small"} />,
        bgColor: colors.decorativeSurfaceThreeL,
        secondaryIcon: {
          dsIcon: <MentionOutlined size={"xxx-small"} variant="on" />,
          bgColor: colors.surfaceOnError,
        },
      },
      FA: {
        dsIcon: (
          <Acronym size={"x-small"} color={colors.yellow400} name={"FA"} />
        ),
        bgColor: colors.yellow950,
      },
      FA_AUDIO: {
        dsIcon: (
          <Acronym size={"x-small"} color={colors.yellow400} name={"FA"} />
        ),
        bgColor: colors.yellow950,
      },
      FA_AUDIO_TAGGED: {
        dsIcon: (
          <Acronym size={"x-small"} color={colors.yellow400} name={"FA"} />
        ),
        bgColor: colors.yellow950,
        secondaryIcon: {
          dsIcon: <MentionOutlined size={"xxx-small"} variant="on" />,
          bgColor: colors.surfaceOnError,
        },
      },
      SA: {
        dsIcon: <Acronym size={"x-small"} color={colors.teal400} name={"SA"} />,
        bgColor: colors.teal950,
      },
      SA_AUDIO: {
        dsIcon: <Acronym size={"x-small"} color={colors.teal400} name={"SA"} />,
        bgColor: colors.teal950,
      },
      SA_AUDIO_TAGGED: {
        dsIcon: <Acronym size={"x-small"} color={colors.teal400} name={"SA"} />,
        bgColor: colors.teal950,
        secondaryIcon: {
          dsIcon: <MentionOutlined size={"xxx-small"} variant="on" />,
          bgColor: colors.surfaceOnError,
        },
      },
      LE_DEFAULT: {
        dsIcon: (
          <Acronym size={"x-small"} color={colors.violet300} name={"LE"} />
        ),
        bgColor: colors.violet900,
      },
      LE_TAGGED: {
        dsIcon: (
          <Acronym size={"x-small"} color={colors.violet300} name={"LE"} />
        ),
        bgColor: colors.violet900,
        secondaryIcon: {
          dsIcon: <MentionOutlined size={"xxx-small"} variant="on" />,
          bgColor: colors.surfaceOnError,
        },
      },
      FA_DEFAULT: {
        dsIcon: (
          <Acronym size={"x-small"} color={colors.yellow400} name={"FA"} />
        ),
        bgColor: colors.yellow950,
      },
      FA_TAGGED: {
        dsIcon: (
          <Acronym size={"x-small"} color={colors.yellow400} name={"FA"} />
        ),
        bgColor: colors.yellow950,
        secondaryIcon: {
          dsIcon: <MentionOutlined size={"xxx-small"} variant="on" />,
          bgColor: colors.surfaceOnError,
        },
      },
      SA_DEFAULT: {
        dsIcon: <Acronym size={"x-small"} color={colors.teal400} name={"SA"} />,
        bgColor: colors.teal950,
      },
      SA_TAGGED: {
        dsIcon: <Acronym size={"x-small"} color={colors.teal400} name={"SA"} />,
        bgColor: colors.teal950,
        secondaryIcon: {
          dsIcon: <MentionOutlined size={"xxx-small"} variant="on" />,
          bgColor: colors.surfaceOnError,
        },
      },
      DA_DEFAULT: {
        dsIcon: <Acronym size={"x-small"} color={colors.blue300} name={"DA"} />,
        bgColor: colors.blue900,
      },
      DA_TAGGED: {
        dsIcon: <Acronym size={"x-small"} color={colors.blue300} name={"DA"} />,
        bgColor: colors.blue900,
        secondaryIcon: {
          dsIcon: <MentionOutlined size={"xxx-small"} variant="on" />,
          bgColor: colors.surfaceOnError,
        },
      },
      PA_DEFAULT: {
        dsIcon: <Acronym size={"x-small"} color={colors.teal300} name={"PA"} />,
        bgColor: colors.teal900,
      },
      PA_TAGGED: {
        dsIcon: <Acronym size={"x-small"} color={colors.teal300} name={"PA"} />,
        bgColor: colors.teal900,
        secondaryIcon: {
          dsIcon: <MentionOutlined size={"xxx-small"} variant="on" />,
          bgColor: colors.surfaceOnError,
        },
      },
      OA_DEFAULT: {
        dsIcon: (
          <Acronym size={"x-small"} color={colors.violet300} name={"OA"} />
        ),
        bgColor: colors.violet900,
      },
      OA_TAGGED: {
        dsIcon: (
          <Acronym size={"x-small"} color={colors.violet300} name={"OA"} />
        ),
        bgColor: colors.violet900,
        secondaryIcon: {
          dsIcon: <MentionOutlined size={"xxx-small"} variant="on" />,
          bgColor: colors.surfaceOnError,
        },
      },
      QT_DEFAULT: {
        dsIcon: <Acronym size={"x-small"} color={colors.blue300} name={"QT"} />,
        bgColor: colors.blue900,
      },
      QT_TAGGED: {
        dsIcon: <Acronym size={"x-small"} color={colors.blue300} name={"QT"} />,
        bgColor: colors.blue900,
        secondaryIcon: {
          dsIcon: <MentionOutlined size={"xxx-small"} variant="on" />,
          bgColor: colors.surfaceOnError,
        },
      },
      SE_DEFAULT: {
        dsIcon: (
          <Acronym size={"x-small"} color={colors.yellow300} name={"SE"} />
        ),
        bgColor: colors.yellow900,
      },
      SE_TAGGED: {
        dsIcon: (
          <Acronym size={"x-small"} color={colors.yellow300} name={"SE"} />
        ),
        bgColor: colors.yellow900,
        secondaryIcon: {
          dsIcon: <MentionOutlined size={"xxx-small"} variant="on" />,
          bgColor: colors.surfaceOnError,
        },
      },
      PT_DEFAULT: {
        dsIcon: <Acronym size={"x-small"} color={colors.blue300} name={"PT"} />,
        bgColor: colors.blue950,
      },
      PT_TAGGED: {
        dsIcon: <Acronym size={"x-small"} color={colors.blue300} name={"PT"} />,
        bgColor: colors.blue950,
        secondaryIcon: {
          dsIcon: <MentionOutlined size={"xxx-small"} variant="on" />,
          bgColor: colors.surfaceOnError,
        },
      },
      BA_DEFAULT: {
        dsIcon: <Acronym size={"x-small"} color={colors.blue400} name={"BA"} />,
        bgColor: colors.blue950,
      },
      BA_TAGGED: {
        dsIcon: <Acronym size={"x-small"} color={colors.blue400} name={"BA"} />,
        bgColor: colors.blue950,
        secondaryIcon: {
          dsIcon: <MentionOutlined size={"xxx-small"} variant="on" />,
          bgColor: colors.surfaceOnError,
        },
      },
      DP_PA_DEFAULT: {
        dsIcon: (
          <Acronym size={"x-small"} color={colors.yellow400} name={"PA"} />
        ),
        bgColor: colors.yellow950,
      },
      DP_PA_TAGGED: {
        dsIcon: (
          <Acronym size={"x-small"} color={colors.yellow400} name={"PA"} />
        ),
        bgColor: colors.yellow950,
        secondaryIcon: {
          dsIcon: <MentionOutlined size={"xxx-small"} variant="on" />,
          bgColor: colors.surfaceOnError,
        },
      },
      AFL_DEFAULT: {
        dsIcon: (
          <Acronym size={"small"} color={colors.yellow300} name={"AFL"} />
        ),
        bgColor: colors.yellow900,
      },
      AFL_TAGGED: {
        dsIcon: (
          <Acronym size={"small"} color={colors.yellow300} name={"AFL"} />
        ),
        bgColor: colors.yellow900,
        secondaryIcon: {
          dsIcon: <MentionOutlined size={"xxx-small"} variant="on" />,
          bgColor: colors.surfaceOnError,
        },
      },
      AOL_DEFAULT: {
        dsIcon: <Acronym size={"small"} color={colors.teal300} name={"AOL"} />,
        bgColor: colors.teal900,
      },
      AOL_TAGGED: {
        dsIcon: <Acronym size={"small"} color={colors.teal300} name={"AOL"} />,
        bgColor: colors.teal900,
        secondaryIcon: {
          dsIcon: <MentionOutlined size={"xxx-small"} variant="on" />,
          bgColor: colors.surfaceOnError,
        },
      },
      WS_DEFAULT: {
        dsIcon: (
          <Acronym size={"x-small"} color={colors.violet300} name={"WS"} />
        ),
        bgColor: colors.violet900,
      },
      WS_TAGGED: {
        dsIcon: (
          <Acronym size={"x-small"} color={colors.violet300} name={"WS"} />
        ),
        bgColor: colors.violet900,
        secondaryIcon: {
          dsIcon: <MentionOutlined size={"xxx-small"} variant="on" />,
          bgColor: colors.surfaceOnError,
        },
      },
    },
  },
  COMMENT_UNIT_PLAN: {
    label: "common:unit_plan",
    dsIcon: (
      <UnitPlanOutlined
        size={"xx-small"}
        variant="success"
        color={colors.teal300}
      />
    ),
    bgColor: colors.teal900,
    stackedIcons: true,
    subCategories: {
      TAGGED: {
        label: "common:unit_plan",
        dsIcon: (
          <UnitPlanOutlined
            size={"xx-small"}
            variant="success"
            color={colors.teal300}
          />
        ),
        bgColor: colors.teal900,
        secondaryIcon: {
          dsIcon: <MentionOutlined size={"xxx-small"} variant="on" />,
          bgColor: colors.surfaceOnError,
        },
      },
      DEFAULT: {
        label: "common:unit_plan",
        dsIcon: (
          <UnitPlanOutlined
            size={"xx-small"}
            variant="success"
            color={colors.teal300}
          />
        ),
        bgColor: colors.teal900,
      },
      AUDIO: {
        dsIcon: (
          <UnitPlanOutlined
            size={"xx-small"}
            variant="success"
            color={colors.teal300}
          />
        ),
        bgColor: colors.teal900,
      },
      AUDIO_TAGGED: {
        dsIcon: (
          <UnitPlanOutlined
            size={"xx-small"}
            variant="success"
            color={colors.teal300}
          />
        ),
        bgColor: colors.teal900,
        secondaryIcon: {
          dsIcon: <MentionOutlined size={"xxx-small"} variant="on" />,
          bgColor: colors.surfaceOnError,
        },
      },
    },
  },
  COLLABORATOR: {
    label: "common:unit_plan",
    dsIcon: (
      <UnitPlanOutlined
        size={"xx-small"}
        variant="success"
        color={colors.teal300}
      />
    ),
    bgColor: colors.teal900,
    stackedIcons: true,
  },
  UNIT_PLAN_SHARED: {
    label: "common:unit_plan",
    dsIcon: (
      <UnitPlanOutlined
        size={"xx-small"}
        variant="success"
        color={colors.teal300}
      />
    ),
    bgColor: colors.teal900,
    stackedIcons: true,
  },
  CLASS_MATERIALS_RESOURCE: {
    dsIcon: <PlaceholderFileOutlined size={"xx-small"} variant="link" />,
    bgColor: colors.blue900,
    subCategories: {
      DEFAULT: {
        dsIcon: <PlaceholderFileOutlined size={"xx-small"} variant="link" />,
        bgColor: colors.blue900,
        stackedIcons: true,
      },
      SHARED_RESOURCE_INSIDE_SECTION: {
        dsIcon: <PlaceholderFileOutlined size={"xx-small"} variant="link" />,
        bgColor: colors.blue900,
      },
      SHARED_SECTION: {
        dsIcon: <SubsectionListOutlined size={"xx-small"} variant="warning" />,
        bgColor: colors.yellow900,
        stackedIcons: true,
      },
      IMAGE: {
        dsIcon: <ImageSquareOutlined variant="success" size={"xx-small"} />,
        bgColor: colors.teal900,
      },
      VIDEO: {
        dsIcon: <VideoOutlined variant="critical" size={"xx-small"} />,
        bgColor: colors.pink900,
      },
      LINK: {
        dsIcon: <LinkOutlined variant="link" size={"xx-small"} />,
        bgColor: colors.blue900,
      },
      NOTE: {
        dsIcon: <NoteOutlined variant="warning" size={"xx-small"} />,
        bgColor: colors.yellow900,
      },
      PDF: {
        dsIcon: <PdfOutlined variant="critical" size={"xx-small"} />,
        bgColor: colors.pink900,
      },
      ZIP: {
        dsIcon: <ZipFileOutlined variant="warning" size={"xx-small"} />,
        bgColor: colors.yellow900,
      },
      SHEET: {
        dsIcon: <SheetsOutlined variant="success" size={"xx-small"} />,
        bgColor: colors.teal900,
      },
      SLIDES: {
        dsIcon: <SlidesOutlined variant="warning" size={"xx-small"} />,
        bgColor: colors.yellow900,
      },
      AUDIO: {
        dsIcon: <AudioColored size={"xx-small"} />,
        bgColor: colors.violet900,
      },
    },
  },
  COMMENT_POST: {
    label: "common:comment",
    dsIcon: <CommentMultipleColored size={"x-small"} />,
    bgColor: colors.decorativeSurfaceThreeL,
    stackedIcons: true,
  },
  COMMENT_ROUTINE_POST: {
    label: "common:comment",
    dsIcon: <CommentMultipleColored size={"x-small"} />,
    bgColor: colors.decorativeSurfaceThreeL,
    stackedIcons: true,
  },
  POST_APPROVAL: {
    label: "journal:approve_post",
    dsIcon: <PostOneColored size={"x-small"} />,
    bgColor: colors.decorativeSurfaceFourL,
    stackedIcons: true,
  },
  TEACHER_POST_APPROVAL: {
    label: "journal:approve_post",
    dsIcon: <PostOneColored size={"x-small"} />,
    bgColor: colors.decorativeSurfaceFourL,
    stackedIcons: true,
  },
  POST_APPROVAL_WITH_COMMENT: {
    label: "notifications:notif_title_approval_with_comment",
    dsIcon: <PostOneColored size={"x-small"} />,
    bgColor: colors.decorativeSurfaceFourL,
  },
  POST_REJECT_WITH_COMMENT: {
    label: "notifications:notif_title_reject_with_comment",
    dsIcon: <PostOneColored size={"x-small"} />,
    bgColor: colors.decorativeSurfaceFourL,
  },
  STUDENT_POST_APPROVED: {
    label: "common:new_post",
    dsIcon: <PostOneColored size={"x-small"} />,
    bgColor: colors.decorativeSurfaceFourL,
  },
  STUDENT_POST_REJECTED: {
    label: "common:new_post",
    dsIcon: <PostOneColored size={"x-small"} />,
    bgColor: colors.decorativeSurfaceFourL,
  },
  STUDENT_POST_PUBLISHED: {
    label: "common:new_post",
    dsIcon: <PostOneColored size={"x-small"} />,
    bgColor: colors.decorativeSurfaceFourL,
    subCategories: {
      NOTE: {
        dsIcon: <NoteColored size={"x-small"} />,
        bgColor: colors.decorativeSurfaceThreeL,
      },
      WORKBOOK: {
        dsIcon: <WorkbookColored size={"x-small"} />,
        bgColor: colors.decorativeSurfaceThreeL,
      },
      FILE: {
        dsIcon: <GeneralAssessmentColored size={"x-small"} />,
        bgColor: colors.decorativeSurfaceThreeL,
      },
      LINK: {
        dsIcon: <LinkColored size={"x-small"} />,
        bgColor: colors.decorativeSurfaceFiveL,
      },
      AUDIO: {
        dsIcon: <AudioColored size={"x-small"} />,
        bgColor: colors.decorativeSurfaceTwoL,
      },
      VIDEO: {
        dsIcon: <VideoColored size={"x-small"} />,
        bgColor: colors.decorativeSurfaceFourL,
      },
      IMAGE: {
        dsIcon: <ImageSquareColored size={"x-small"} />,
        bgColor: colors.decorativeSurfaceFiveL,
      },
      POST: {
        dsIcon: <PostOneColored size={"x-small"} />,
        bgColor: colors.decorativeSurfaceFourL,
      },
    },
  },

  // Projects notifications
  PROJECT_SUPERVISEE_ASSIGNED: {
    subCategories: commonProjectNotificationSubCategories,
  },
  PROJECT_SUPERVISOR_ASSIGNED: {
    subCategories: commonProjectNotificationSubCategories,
  },
  PROJECT_REPORT_STATUS_UPDATED: {
    subCategories: commonProjectNotificationSubCategories,
  },
  PROJECT_OVERALL_STATUS_CHANGED: {
    subCategories: commonProjectNotificationSubCategories,
  },
  PROJECT_FINAL_REPORT_SUBMITTED: {
    subCategories: commonProjectNotificationSubCategories,
  },
  PROJECT_DRAFT_REPORT_SUBMITTED: {
    subCategories: commonProjectNotificationSubCategories,
  },
  PROJECT_COMMENT: {
    subCategories: commonProjectNotificationSubCategories,
  },
  PROJECT_GROUP_TASK_CREATED: {
    subCategories: commonProjectNotificationSubCategories,
  },
  PROJECT_GROUP_TASK_SUBMISSION_ENABLED: {
    subCategories: commonProjectNotificationSubCategories,
  },
  PROJECT_GROUP_DEADLINE_CLOSED_DATE_UPDATED: {
    subCategories: commonProjectNotificationSubCategories,
  },
  PROJECT_GROUP_DEADLINE_DUE_DATE_UPDATED: {
    subCategories: commonProjectNotificationSubCategories,
  },
  PROJECT_GROUP_SUBMISSION_CLOSED_AUTOMATICALLY: {
    subCategories: commonProjectNotificationSubCategories,
  },
  PROJECT_SUBMISSION_ITEM_RESPONSE_ANNOTATED: {
    subCategories: commonProjectNotificationSubCategories,
  },
  PROJECT_SUBMISSION_TURNED_IN: {
    subCategories: commonProjectNotificationSubCategories,
  },
  PROJECT_SUBMISSION_TURNED_IN_BY_ALL: {
    subCategories: commonProjectNotificationSubCategories,
  },
  PROJECT_TASK_MARKED_COMPLETED: {
    subCategories: commonProjectNotificationSubCategories,
  },
  PROJECT_GROUP_TASK_REMINDER: {
    subCategories: commonProjectNotificationSubCategories,
  },
  PROJECT_SUBMISSION_LOCKED: {
    subCategories: commonProjectNotificationSubCategories,
  },
  PROJECT_SUBMISSION_UNLOCKED: {
    subCategories: commonProjectNotificationSubCategories,
  },
  PROJECT_COMMENT_MENTION: {
    subCategories: {
      PERSONAL: {
        studentLabel: "project:personal_project",
        dsIcon: <Acronym size={"x-small"} color={colors.teal300} name={"PP"} />,
        bgColor: colors.teal900,
        secondaryIcon: {
          dsIcon: <MentionOutlined variant={"on"} size={"xxx-small"} />,
          bgColor: colors.surfaceOnError,
        },
      },
      COMMUNITY: {
        studentLabel: "project:community_project",
        dsIcon: <Acronym size={"x-small"} color={colors.pink300} name={"CP"} />,
        bgColor: colors.pink900,
        secondaryIcon: {
          dsIcon: <MentionOutlined variant={"on"} size={"xxx-small"} />,
          bgColor: colors.surfaceOnError,
        },
      },
      SERVICE_AS_ACTION: {
        studentLabel: "project:service_as_action",
        dsIcon: (
          <Acronym size={"x-small"} color={colors.violet300} name={"SAA"} />
        ),
        bgColor: colors.violet900,
        secondaryIcon: {
          dsIcon: <MentionOutlined variant={"on"} size={"xxx-small"} />,
          bgColor: colors.surfaceOnError,
        },
      },
      DP_CAS: {
        studentLabel: "project:creativity_action_service",
        dsIcon: (
          <Acronym size={"x-small"} color={colors.yellow300} name={"CAS"} />
        ),
        bgColor: colors.yellow900,
        secondaryIcon: {
          dsIcon: <MentionOutlined variant={"on"} size={"xxx-small"} />,
          bgColor: colors.surfaceOnError,
        },
      },
      DP_EE: {
        studentLabel: "project:extended_essay",
        dsIcon: <Acronym size={"x-small"} color={colors.pink300} name={"EE"} />,
        bgColor: colors.pink900,
        secondaryIcon: {
          dsIcon: <MentionOutlined variant={"on"} size={"xxx-small"} />,
          bgColor: colors.surfaceOnError,
        },
      },
      DP_TOK_ESSAY: {
        studentLabel: "project:tok_essay_sentence_case",
        dsIcon: (
          <Acronym size={"x-small"} color={colors.yellow300} name={"TOK"} />
        ),
        bgColor: colors.yellow900,
        secondaryIcon: {
          dsIcon: <MentionOutlined variant={"on"} size={"xxx-small"} />,
          bgColor: colors.surfaceOnError,
        },
      },
      DP_TOK_EXHIBITION: {
        studentLabel: "project:tok_exhibition_sentence_case",
        dsIcon: (
          <Acronym size={"x-small"} color={colors.yellow300} name={"TOK"} />
        ),
        bgColor: colors.yellow900,
        secondaryIcon: {
          dsIcon: <MentionOutlined variant={"on"} size={"xxx-small"} />,
          bgColor: colors.surfaceOnError,
        },
      },
      INTERNAL_ASSESSMENT: {
        dsIcon: <Acronym size={"x-small"} color={colors.blue300} name={"IA"} />,
        bgColor: colors.blue900,
        secondaryIcon: {
          dsIcon: <MentionOutlined variant={"on"} size={"xxx-small"} />,
          bgColor: colors.surfaceOnError,
        },
      },
      EXTERNAL_ASSESSMENT: {
        dsIcon: <Acronym size={"x-small"} color={colors.teal300} name={"EA"} />,
        bgColor: colors.teal900,
        secondaryIcon: {
          dsIcon: <MentionOutlined variant={"on"} size={"xxx-small"} />,
          bgColor: colors.surfaceOnError,
        },
      },
      DP_G4_PROJECT: {
        studentLabel: "project:group_4_project",
        dsIcon: <Acronym size={"x-small"} color={colors.teal300} name={"G4"} />,
        bgColor: colors.teal900,
        secondaryIcon: {
          dsIcon: <MentionOutlined variant={"on"} size={"xxx-small"} />,
          bgColor: colors.surfaceOnError,
        },
      },
      CP_REFLECTIVE_PROJECT: {
        studentLabel: "project:reflective_project",
        dsIcon: <Acronym size={"x-small"} color={colors.pink300} name={"RP"} />,
        bgColor: colors.pink900,
        secondaryIcon: {
          dsIcon: <MentionOutlined variant={"on"} size={"xxx-small"} />,
          bgColor: colors.surfaceOnError,
        },
      },
      CP_SERVICE_LEARNING: {
        studentLabel: "project:service_learning",
        dsIcon: (
          <Acronym size={"x-small"} color={colors.yellow300} name={"SL"} />
        ),
        bgColor: colors.yellow900,
        secondaryIcon: {
          dsIcon: <MentionOutlined variant={"on"} size={"xxx-small"} />,
          bgColor: colors.surfaceOnError,
        },
      },
      COMMUNITY_ENGAGEMENT: {
        studentLabel: "project:community_engagement",
        dsIcon: (
          <Acronym size={"x-small"} color={colors.yellow300} name={"CE"} />
        ),
        bgColor: colors.yellow900,
        secondaryIcon: {
          dsIcon: <MentionOutlined variant={"on"} size={"xxx-small"} />,
          bgColor: colors.surfaceOnError,
        },
      },
      DP_COLLABORATIVE_SCIENCES_PROJECT: {
        studentLabel: "project:dp_collaborative_sciences_project",
        dsIcon: (
          <Acronym size={"x-small"} color={colors.teal300} name={"CSP"} />
        ),
        bgColor: colors.teal900,
        secondaryIcon: {
          dsIcon: <MentionOutlined variant={"on"} size={"xxx-small"} />,
          bgColor: colors.surfaceOnError,
        },
      },
    },
  },
  PROJECT_POST_COMMENT: {
    subCategories: commonProjectNotificationSubCategories,
  },
  PROJECT_ATTACHMENT_ANNOTATED: {
    subCategories: commonProjectNotificationSubCategories,
  },
  PROJECT_ACADEMIC_HONESTY_SUBMITTED: {
    subCategories: commonProjectNotificationSubCategories,
  },
  PROJECT_DEADLINE_REMINDER: {
    subCategories: {
      PERSONAL: {
        studentLabel: "project:personal_project",
        dsIcon: <Acronym size={"x-small"} color={colors.teal300} name={"PP"} />,
        bgColor: colors.teal900,
        secondaryIcon: {
          dsIcon: <WarningTriangleColored variant={"on"} size={"xxx-small"} />,
        },
      },
      COMMUNITY: {
        studentLabel: "project:community_project",
        dsIcon: <Acronym size={"x-small"} color={colors.pink300} name={"CP"} />,
        bgColor: colors.pink900,
        secondaryIcon: {
          dsIcon: <WarningTriangleColored variant={"on"} size={"xxx-small"} />,
        },
      },
      SERVICE_AS_ACTION: {
        studentLabel: "project:service_as_action",
        dsIcon: (
          <Acronym size={"x-small"} color={colors.violet300} name={"SAA"} />
        ),
        bgColor: colors.violet900,
        secondaryIcon: {
          dsIcon: <WarningTriangleColored variant={"on"} size={"xxx-small"} />,
        },
      },
      DP_CAS: {
        studentLabel: "project:creativity_action_service",
        dsIcon: (
          <Acronym size={"x-small"} color={colors.yellow300} name={"CAS"} />
        ),
        bgColor: colors.yellow900,
        secondaryIcon: {
          dsIcon: <WarningTriangleColored variant={"on"} size={"xxx-small"} />,
        },
      },
      DP_EE: {
        studentLabel: "project:extended_essay",
        dsIcon: <Acronym size={"x-small"} color={colors.pink300} name={"EE"} />,
        bgColor: colors.pink900,
        secondaryIcon: {
          dsIcon: <WarningTriangleColored variant={"on"} size={"xxx-small"} />,
        },
      },
      DP_TOK_ESSAY: {
        studentLabel: "project:tok_essay_sentence_case",
        dsIcon: (
          <Acronym size={"x-small"} color={colors.yellow300} name={"TOK"} />
        ),
        bgColor: colors.yellow900,
        secondaryIcon: {
          dsIcon: (
            <WarningTriangleFilled variant={"warning"} size={"xxx-small"} />
          ),
        },
      },
      DP_TOK_EXHIBITION: {
        studentLabel: "project:tok_exhibition_sentence_case",
        dsIcon: (
          <Acronym size={"x-small"} color={colors.yellow300} name={"TOK"} />
        ),
        bgColor: colors.yellow900,
        secondaryIcon: {
          dsIcon: <WarningTriangleColored variant={"on"} size={"xxx-small"} />,
        },
      },
      INTERNAL_ASSESSMENT: {
        dsIcon: <Acronym size={"x-small"} color={colors.blue300} name={"IA"} />,
        bgColor: colors.blue900,
        secondaryIcon: {
          dsIcon: <WarningTriangleColored variant={"on"} size={"xxx-small"} />,
        },
      },
      EXTERNAL_ASSESSMENT: {
        dsIcon: <Acronym size={"x-small"} color={colors.teal300} name={"EA"} />,
        bgColor: colors.teal900,
        secondaryIcon: {
          dsIcon: <WarningTriangleColored variant={"on"} size={"xxx-small"} />,
        },
      },
      DP_G4_PROJECT: {
        studentLabel: "project:group_4_project",
        dsIcon: <Acronym size={"x-small"} color={colors.teal300} name={"G4"} />,
        bgColor: colors.teal900,
        secondaryIcon: {
          dsIcon: <WarningTriangleColored variant={"on"} size={"xxx-small"} />,
        },
      },
      DP_COLLABORATIVE_SCIENCES_PROJECT: {
        studentLabel: "project:dp_collaborative_sciences_project",
        dsIcon: (
          <Acronym size={"x-small"} color={colors.teal300} name={"CSP"} />
        ),
        bgColor: colors.teal900,
        secondaryIcon: {
          dsIcon: <WarningTriangleColored variant={"on"} size={"xxx-small"} />,
        },
      },
      CP_REFLECTIVE_PROJECT: {
        studentLabel: "project:reflective_project",
        dsIcon: <Acronym size={"x-small"} color={colors.pink300} name={"RP"} />,
        bgColor: colors.pink900,
        secondaryIcon: {
          dsIcon: <WarningTriangleColored variant={"on"} size={"xxx-small"} />,
        },
      },
    },
  },
  SERVICE_AS_ACTION_INVITATION: {
    subCategories: commonProjectNotificationSubCategories,
  },
  PROJECT_GROUP_SUPERVISOR_APPROVAL_REQUESTED: {
    subCategories: commonProjectNotificationSubCategories,
  },
  PROJECT_GROUP_SUPERVISOR_APPROVAL_REQUEST_REJECTED: {
    subCategories: commonProjectNotificationSubCategories,
  },
  CAS_ADVISEE_ASSIGNED: {
    subCategories: commonProjectNotificationSubCategories,
  },
  CAS_ADVISOR_ASSIGNED: {
    subCategories: commonProjectNotificationSubCategories,
  },
  PORTFOLIO_ADVISOR_ASSIGNED: {
    subCategories: commonProjectNotificationSubCategories,
  },
  PORTFOLIO_ADVISEE_ASSIGNED: {
    subCategories: commonProjectNotificationSubCategories,
  },
  PORTFOLIO_COMMENT: {
    subCategories: commonProjectNotificationSubCategories,
  },
  PORTFOLIO_COMMENT_MENTION: {
    subCategories: {
      DP_CAS: {
        studentLabel: "project:creativity_action_service",
        dsIcon: (
          <Acronym size={"x-small"} color={colors.yellow300} name={"CAS"} />
        ),
        bgColor: colors.yellow900,
        secondaryIcon: {
          dsIcon: <MentionOutlined variant={"on"} size={"xxx-small"} />,
          bgColor: colors.surfaceOnError,
        },
      },
      CP_SERVICE_LEARNING: {
        studentLabel: "project:service_learning",
        dsIcon: (
          <Acronym size={"x-small"} color={colors.yellow300} name={"SL"} />
        ),
        bgColor: colors.yellow900,
        secondaryIcon: {
          dsIcon: <MentionOutlined variant={"on"} size={"xxx-small"} />,
          bgColor: colors.surfaceOnError,
        },
      },
      COMMUNITY_ENGAGEMENT: {
        studentLabel: "project:community_engagement",
        dsIcon: (
          <Acronym size={"x-small"} color={colors.yellow300} name={"CE"} />
        ),
        bgColor: colors.yellow900,
        secondaryIcon: {
          dsIcon: <MentionOutlined variant={"on"} size={"xxx-small"} />,
          bgColor: colors.surfaceOnError,
        },
      },
    },
  },
  CAS_ADVISOR_FEEDBACK_ADDED: {
    subCategories: commonProjectNotificationSubCategories,
  },
  CAS_OVERALL_STATUS_CHANGED: {
    subCategories: commonProjectNotificationSubCategories,
  },
  CAS_LO_ACHIEVED: {
    subCategories: commonProjectNotificationSubCategories,
  },
  CAS_INTERVIEW_MARKED_AS_COMPLETE: {
    subCategories: commonProjectNotificationSubCategories,
  },
  PORTFOLIO_POST_COMMENT: {
    subCategories: commonProjectNotificationSubCategories,
  },
  LO_COMMENT: {
    subCategories: commonProjectNotificationSubCategories,
  },
  CAS_PROJECT_CREATED: {
    studentLabel: "project:creativity_action_service",
    dsIcon: <Acronym size={"x-small"} color={colors.yellow300} name={"CAS"} />,
    bgColor: colors.yellow900,
  },
  CAS_PROJECT_DELETED: {
    studentLabel: "project:creativity_action_service",
    dsIcon: <Acronym size={"x-small"} color={colors.yellow300} name={"CAS"} />,
    bgColor: colors.yellow900,
  },
  PORTFOLIO_MARKED_AS_COMPLETED: {
    subCategories: commonProjectNotificationSubCategories,
  },
  PORTFOLIO_OVERALL_STATUS_CHANGED: {
    subCategories: commonProjectNotificationSubCategories,
  },
  PORTFOLIO_LO_ACHIEVED: {
    subCategories: commonProjectNotificationSubCategories,
  },
  PORTFOLIO_ADVISOR_FEEDBACK_ADDED: {
    subCategories: commonProjectNotificationSubCategories,
  },
  PORTFOLIO_PROJECT_CREATED: {
    subCategories: commonProjectNotificationSubCategories,
  },
  PORTFOLIO_PROJECT_DELETED: {
    subCategories: commonProjectNotificationSubCategories,
  },
  PROJECT_ACADEMIC_HONESTY_LOCKED: {
    subCategories: commonProjectNotificationSubCategories,
  },
  PROJECT_ACADEMIC_HONESTY_UNLOCKED: {
    subCategories: commonProjectNotificationSubCategories,
  },
  PROGRESS_REPORT: {
    label: "Progress report",
    dsIcon: (
      <ProgressReportOutlined
        size={"xx-small"}
        color={colors.teal400}
        variant={"success"}
      />
    ),
    bgColor: colors.teal950,
  },
  PROGRESS_REPORT_CREATION: {
    label: "Progress report",
    dsIcon: (
      <ProgressReportOutlined
        size={"xx-small"}
        color={colors.teal400}
        variant={"success"}
      />
    ),
    bgColor: colors.teal950,
  },
  PROGRESS_REPORT_EVALUTATION: {
    label: "Progress report",
    dsIcon: (
      <ProgressReportOutlined
        size={"xx-small"}
        color={colors.teal400}
        variant={"success"}
      />
    ),
    bgColor: colors.teal950,
  },
  PROJECT_REVIEW_LOG_ADDED: {
    subCategories: commonProjectNotificationSubCategories,
  },
  PROJECT_REVIEW_LOG_COMMENT_ADDED: {
    subCategories: commonProjectNotificationSubCategories,
  },
  PROJECT_REVIEW_LOG_RESOLVED: {
    subCategories: commonProjectNotificationSubCategories,
  },
  PROJECT_REVIEW_LOG_UNRESOLVED: {
    subCategories: commonProjectNotificationSubCategories,
  },
  REJECTED_FAMILY_CLASSLINK_INVITATION: {
    label: "notifications:notif_rejected_family_class_invite_title_v2",
    dsIcon: <EnvelopePlainOutlined variant={"warning"} size={"xx-small"} />,
    bgColor: colors.decorativeSurfaceThreeL,
  },
  DP_EXAM_APPROVAL: {
    label: "notifications:notif_exam_reg_approval_title_v2",
    dsIcon: (
      <ClipboardTickOutlined
        size={"xx-small"}
        color={colors.teal300}
        variant={"success"}
      />
    ),
    bgColor: colors.teal900,
  },
  CLASSLINK_APPROVAL_REQUEST: {
    label: "notifications:notif_classlink_approval_request_title",
    dsIcon: <EnvelopePlainOutlined variant={"warning"} size={"xx-small"} />,
    bgColor: colors.decorativeSurfaceThreeL,
  },
  PENDING_FAMILY_EMAIL_INVITATION: {
    label: "notifications:notif_pending_family_email_invite_title_v2",
    dsIcon: <EnvelopePlainOutlined variant={"warning"} size={"xx-small"} />,
    bgColor: colors.decorativeSurfaceThreeL,
  },
  //SNP notifications
  SNP_CHAT: {
    label: "notifications:notif_auth_and_eval_title",
    dsIcon: (
      <AssessmentEvaluatedOutlined size={"xx-small"} variant="critical" />
    ),
    bgColor: colors.pink900,
    stackedIcons: true,
  },
  SNP_CYCLE_CREATION: {
    label: "notifications:notif_auth_and_eval_title",
    dsIcon: (
      <AssessmentEvaluatedOutlined size={"xx-small"} variant="critical" />
    ),
    bgColor: colors.pink900,
    stackedIcons: true,
  },
  ASSIGNMENT_CREATE: {
    svgIcon: <IcnNotiAddedPost fill={colors.white} width={32} height={32} />,
    bgColor: colors.blue400,
    subCategories: getCommonClassRoomNotificationSubCategories(),
  },
  STUDENT_ASSIGNMENT_OVERRIDE_STATUS: {
    label: "notifications:notif_student_submission_override_status",
    familyLabel: "notifications:notif_family_submission_override_status",
    svgIcon: <SubmissionIcon fill={colors.white} width={20} height={20} />,
    bgColor: colors.teal200,
    subCategories: getCommonClassRoomNotificationSubCategories(),
  },
  STUDENT_ASSIGNMENT_OVERRIDE_RESET_STATUS: {
    label: "notifications:notif_student_submission_override_status",
    familyLabel: "notifications:notif_family_submission_override_status",
    svgIcon: <SubmissionIcon fill={colors.white} width={20} height={20} />,
    bgColor: colors.teal200,
    subCategories: getCommonClassRoomNotificationSubCategories(),
  },
  STUDENT_ASSIGNMENT_SUBMISSION: {
    label: "notifications:handed_in_submission",
    svgIcon: <SubmissionIcon fill={colors.white} width={20} height={20} />,
    bgColor: colors.teal200,
    subCategories: getCommonClassRoomNotificationSubCategories(),
  },
  ALL_STUDENT_ASSIGNMENT_SUBMISSION: {
    label: "notifications:submission_complete",
    svgIcon: <SubmissionIcon fill={colors.white} width={20} height={20} />,
    bgColor: colors.teal200,
    subCategories: getCommonClassRoomNotificationSubCategories(),
  },
  STUDENT_ASSIGNMENT_SHARED: {
    label: "assessmentEvaluation:evaluation_complete",
    svgIcon: (
      <IcnNotiEvaluationCompleted fill={colors.white} width={32} height={32} />
    ),
    bgColor: colors.yellow500,
    subCategories: getCommonClassRoomNotificationSubCategories(),
  },
  STUDENT_ASSIGNMENT_SUBMISSION_UNHAND: {
    label: "classRoom:submission_returned_text",
    svgIcon: (
      <IcnNotiSubmissionReturned fill={colors.white} width={32} height={32} />
    ),
    bgColor: colors.yellow500,
    subCategories: _.mapValues(
      getCommonClassRoomNotificationSubCategories(),
      category => {
        return {
          ...category,
          secondaryIcon: {
            dsIcon: (
              <WarningTriangleFilled variant={"critical"} size={"xxx-small"} />
            ),
          },
        };
      }
    ),
  },
  CALENDAR_EVENT_ITEM: {
    label: "notifications:meeting_invitation",
    dsIcon: <VideoColored size={"x-small"} />,
    bgColor: colors.decorativeSurfaceFourL,
  },
  CALENDAR_EVENT_ITEM_RESCHEDULED: {
    label: "notifications:meeting_invitation",
    dsIcon: <VideoColored size={"x-small"} />,
    bgColor: colors.decorativeSurfaceFourL,
  },
  ASSIGNMENT_DEADLINE_REMINDER: {
    subCategories: getCommonClassRoomNotificationSubCategories(),
  },
  ASSIGNMENT_MESSAGE_SENT: {
    label: "notifications:student_message_sent",
    ...classroomMessageIconData,
    subCategories: getCommonClassRoomNotificationSubCategories(true),
  },
  ASSIGNMENT_GROUP_MESSAGE_SENT: {
    label: "notifications:student_group_message_sent",
    ...classroomMessageIconData,
    subCategories: getCommonClassRoomNotificationSubCategories(true),
  },
  REPLIED_MESSAGE: {
    label: "notifications:parent_message_sent",
    ...classroomMessageIconData,
  },
  CHAT_MESSAGE_SENT: {
    label: "notifications:parent_message_sent",
    ...classroomMessageIconData,
    stackedIcons: true,
  },
  STUDENT_ASSIGNMENT_SUBMISSION_LOCK_UPDATED: {
    subCategories: getCommonClassRoomNotificationSubCategories(),
  },
  ASSIGNMENT_CLOSE_DATE_UPDATED: {
    subCategories: getCommonClassRoomNotificationSubCategories(),
  },
  ASSIGNMENT_RESCHEDULED: {
    subCategories: getCommonClassRoomNotificationSubCategories(),
  },
  ASSIGNMENT_DEADLINE_UPDATED: {
    subCategories: getCommonClassRoomNotificationSubCategories(),
  },

  //educator center notification
  TEACHER_RESOURCE: {
    label: "notifications:notif_teacher_resource_shared_title",
    dsIcon: (
      <ResourceOutlined
        size={"xx-small"}
        color={colors.blue300}
        variant="link"
      />
    ),
    bgColor: colors.blue900,
    stackedIcons: true,
  },
  ADMIN_RESOURCE: {
    label: "notifications:notif_admin_resource_shared_title",
    dsIcon: (
      <ResourceOutlined
        size={"xx-small"}
        color={colors.blue300}
        variant="link"
      />
    ),
    bgColor: colors.blue900,
    stackedIcons: true,
  },
  TEACHER_SCHOOL_POLICY: {
    label: "notifications:notif_school_policy_shared_title",
    dsIcon: (
      <PolicyTickOutlined
        size={"xx-small"}
        color={colors.blue300}
        variant="link"
      />
    ),
    bgColor: colors.blue900,
    stackedIcons: true,
  },
  PARENT_SCHOOL_POLICY: {
    label: "notifications:notif_school_policy_shared_title",
    dsIcon: (
      <PolicyTickOutlined
        size={"xx-small"}
        color={colors.blue300}
        variant="link"
      />
    ),
    bgColor: colors.blue900,
    stackedIcons: true,
  },
  STUDENT_DRIVE_RESOURCE: {
    subCategories: {
      DEFAULT: {
        svgIcon: (
          <PlaceholderFileOutlined
            variant="success"
            color={colors.blue300}
            size={"xx-small"}
          />
        ),
        bgColor: colors.blue900,
      },
      MULTI_FILES: {
        svgIcon: (
          <PlaceholderFileOutlined
            variant="success"
            color={colors.blue300}
            size={"xx-small"}
          />
        ),
        bgColor: colors.blue900,
        stackedIcons: true,
      },
      IMAGE: {
        svgIcon: (
          <ImageSquareOutlined
            variant="success"
            color={colors.teal300}
            size={"xx-small"}
          />
        ),
        bgColor: colors.teal900,
      },
      VIDEO: {
        svgIcon: <VideoOutlined color={colors.pink300} size={"xx-small"} />,
        bgColor: colors.pink900,
      },
      LINK: {
        svgIcon: (
          <LinkOutlined
            variant="link"
            color={colors.blue300}
            size={"xx-small"}
          />
        ),
        bgColor: colors.blue900,
      },
      PDF: {
        svgIcon: (
          <PdfOutlined
            variant="critical"
            color={colors.pink300}
            size={"xx-small"}
          />
        ),
        bgColor: colors.pink900,
      },
      ZIP: {
        svgIcon: (
          <ZipFileOutlined
            variant="warning"
            color={colors.yellow300}
            size={"xx-small"}
          />
        ),
        bgColor: colors.yellow900,
      },
      SHEET: {
        svgIcon: (
          <SheetsOutlined
            variant="success"
            color={colors.teal300}
            size={"xx-small"}
          />
        ),
        bgColor: colors.teal900,
      },
      SLIDES: {
        svgIcon: (
          <SlidesOutlined
            variant="warning"
            color={colors.yellow300}
            size={"xx-small"}
          />
        ),
        bgColor: colors.yellow900,
      },
      AUDIO: {
        svgIcon: (
          <VolumeHighOutlined color={colors.violet300} size={"xx-small"} />
        ),
        bgColor: colors.violet900,
      },
      DOCS: {
        svgIcon: (
          <PlaceholderFileOutlined
            variant="success"
            color={colors.teal300}
            size={"xx-small"}
          />
        ),
        bgColor: colors.blue900,
      },
    },
  },
  GOAL_CREATED: {
    label: "studentGoals:goal_plural",
    dsIcon: <GoalsOutlined variant={"success"} size={"x-small"} />,
    bgColor: colors.blue900,
    secondaryIcon: {
      dsIcon: <AddCircleFilled size={"xxx-small"} variant="success" />,
      bgColor: colors.surfaceDefault,
    },
  },
  GOAL_COMPLETED: {
    label: "studentGoals:goal_plural",
    dsIcon: <GoalsOutlined variant={"success"} size={"x-small"} />,
    bgColor: colors.blue900,
    secondaryIcon: {
      dsIcon: <TickCircleFilled size={"xxx-small"} variant="success" />,
      bgColor: colors.surfaceDefault,
    },
  },
  GOAL_MILESTONE_COMPLETED: {
    label: "studentGoals:milestone",
    dsIcon: <GoalsOutlined variant={"success"} size={"x-small"} />,
    bgColor: colors.blue900,
    secondaryIcon: {
      dsIcon: <TickCircleFilled size={"xxx-small"} variant="success" />,
      bgColor: colors.surfaceDefault,
    },
  },
  GOAL_MILESTONE_CREATED: {
    label: "studentGoals:goal_plural",
    dsIcon: <GoalsOutlined variant={"success"} size={"x-small"} />,
    bgColor: colors.blue900,
    secondaryIcon: {
      dsIcon: <AddCircleFilled size={"xxx-small"} variant="success" />,
      bgColor: colors.surfaceDefault,
    },
  },
  GOAL_COMMENT: {
    label: "studentGoals:goal_plural",
    dsIcon: <GoalsOutlined variant={"success"} size={"x-small"} />,
    bgColor: colors.blue900,
  },
  GOAL_MILESTONE_COMMENT: {
    label: "studentGoals:goal_plural",
    dsIcon: <GoalsOutlined variant={"success"} size={"x-small"} />,
    bgColor: colors.blue900,
  },
  PDFTRON_COMMENT_ADDED: {
    label: "classRoom:pdftron_comment_added_text",
    svgIcon: <SubmissionIcon fill={colors.white} width={20} height={20} />,
    bgColor: colors.teal200,
    subCategories: getCommonClassRoomNotificationSubCategories(),
  },
  STUDENT_NOTE_ADDED: {
    label: "notifications:notif_student_note_added_title",
    dsIcon: <StudentOutlined variant={"warning"} size={"xx-small"} />,
    bgColor: colors.yellow900,
  },
  SCHOOL_POLICIES_AND_RESOURCES: {
    subCategories: {
      DEFAULT: {
        dsIcon: (
          <ResourceOutlined
            size={"xx-small"}
            color={colors.blue300}
            variant="link"
          />
        ),
        bgColor: colors.blue900,
      },
      MULTI_RESOURCES: {
        dsIcon: (
          <ResourceOutlined
            size={"xx-small"}
            color={colors.blue300}
            variant="link"
          />
        ),
        bgColor: colors.blue900,
        stackedIcons: true,
      },
    },
  },
  BEHAVIOUR_INCIDENT_UPDATES: {
    subCategories: {
      ENTITY_UPDATE: {
        dsIcon: <HeadHeartOutlined variant="critical" size={"xx-small"} />,
        bgColor: colors.pink900,
      },
      NON_STUDENT_ENTITY_UPDATE: {
        dsIcon: <HeadHeartOutlined variant="critical" size={"xx-small"} />,
        bgColor: colors.pink900,
      },
      INCIDENT_PUBLISHED: {
        dsIcon: <HeadHeartOutlined variant="critical" size={"xx-small"} />,
        bgColor: colors.pink900,
      },
      INCIDENT_RESOLVED: {
        dsIcon: <HeadHeartOutlined variant="critical" size={"xx-small"} />,
        bgColor: colors.pink900,
        secondaryIcon: {
          dsIcon: <TickCircleFilled size={"xxx-small"} variant="success" />,
          bgColor: colors.surfaceDefault,
        },
      },
      INCIDENT_REOPENED: {
        dsIcon: <HeadHeartOutlined variant="critical" size={"xx-small"} />,
        bgColor: colors.pink900,
      },
    },
  },
  BEHAVIOUR_INCIDENT_ACTIONS: {
    subCategories: {
      ACTION: {
        dsIcon: <HeadHeartOutlined variant="critical" size={"xx-small"} />,
        bgColor: colors.pink900,
      },
      ACTION_COMPLETED: {
        dsIcon: <HeadHeartOutlined variant="critical" size={"xx-small"} />,
        bgColor: colors.pink900,
      },
      NON_STUDENT_ACTION_COMPLETED: {
        dsIcon: <HeadHeartOutlined variant="critical" size={"xx-small"} />,
        bgColor: colors.pink900,
      },
    },
  },
  BEHAVIOUR_INCIDENT_ACCESSIBILITY: {
    subCategories: {
      ROLE_SHARED: {
        dsIcon: <HeadHeartOutlined variant="critical" size={"xx-small"} />,
        bgColor: colors.pink900,
      },
      SHARED: {
        dsIcon: <HeadHeartOutlined variant="critical" size={"xx-small"} />,
        bgColor: colors.pink900,
      },
      REFERRED_USER: {
        dsIcon: <HeadHeartOutlined variant="critical" size={"xx-small"} />,
        bgColor: colors.pink900,
        secondaryIcon: {
          dsIcon: <MentionOutlined size={"xxx-small"} variant="on" />,
          bgColor: colors.surfaceOnError,
        },
      },
      REFERRED: {
        dsIcon: <HeadHeartOutlined variant="critical" size={"xx-small"} />,
        bgColor: colors.pink900,
        secondaryIcon: {
          dsIcon: <MentionOutlined size={"xxx-small"} variant="on" />,
          bgColor: colors.surfaceOnError,
        },
      },
      NON_STUDENT_REFERRED: {
        dsIcon: <HeadHeartOutlined variant="critical" size={"xx-small"} />,
        bgColor: colors.pink900,
        secondaryIcon: {
          dsIcon: <MentionOutlined size={"xxx-small"} variant="on" />,
          bgColor: colors.surfaceOnError,
        },
      },
    },
  },
  AI_TUTOR_FLAGGED_MESSAGE: {
    label: "common:ai_tutor",
    dsIcon: <Acronym size={"x-small"} color={colors.purple300} name={"AI"} />,
    bgColor: colors.purple900,
  },
  CALENDAR_EVENT_ITEM_REMINDER: {
    dsIcon: <CalendarOutlined size={"x-small"} variant="warning" />,
    bgColor: colors.yellow900,
  },
  SCHOOL_CALENDAR_EVENT_REMINDER_NOTIFICATION: {
    dsIcon: <CalendarOutlined size={"x-small"} variant="warning" />,
    bgColor: colors.yellow900,
  },
  SCHOOL_CALENDAR_EVENT_CREATE_NOTIFICATION: {
    dsIcon: <CalendarOutlined size={"x-small"} variant="warning" />,
    bgColor: colors.yellow900,
  },
  SCHOOL_CALENDAR_EVENT_UPDATE_NOTIFICATION: {
    dsIcon: <CalendarOutlined size={"x-small"} variant="warning" />,
    bgColor: colors.yellow900,
  },
  VIDEO_MEETING_CREATE_NOTIFICATION: {
    dsIcon: <VideoOutlined size={"x-small"} variant="critical" />,
    bgColor: colors.pink900,
  },
  VIDEO_MEETING_UPDATE_NOTIFICATION: {
    dsIcon: <VideoOutlined size={"x-small"} variant="critical" />,
    bgColor: colors.pink900,
  },
  VIDEO_MEETING_REMINDER_NOTIFICATION: {
    dsIcon: <VideoOutlined size={"x-small"} variant="critical" />,
    bgColor: colors.pink900,
  },
};

export const getNotificationCategoryTabs = ({
  userId,
  userType,
  type,
  isCurriculumProgramFree,
  t,
  getTabLabelComponent,
  curriculumProgramType,
  isAdminPortal,
}) => {
  const notificationCountFromCache = getUserNotificationCountFromCache({
    id: userId,
    type,
    notificationCategoryFilter: getCategoryTabsForNotification({
      isCurriculumProgramFree,
      userType,
    }),
  });

  const categorisedNewCount = _.get(
    notificationCountFromCache,
    "notification.categorisedNewCount",
    []
  );

  const categorisedTotalCount = _.get(
    notificationCountFromCache,
    "notification.categorisedTotalCount",
    []
  );

  const notificationCategory = getNotificationCategory();

  const categoryTabs = _.reduce(
    notificationCategory,
    (categoryTabs, tab) => {
      const { value, filterValue, label, userTypes, paidOnly = false } = tab;

      // Get new notification count for each tab
      let tabNewCount = 0;
      const categoryNewMap = _.keyBy(categorisedNewCount, "category");
      _.forEach(filterValue, value => {
        tabNewCount += _.get(categoryNewMap, `${value}.count`, 0);
      });

      // Get total notification count for each tab
      let tabTotalCount = 0;
      const categoryTotalMap = _.keyBy(categorisedTotalCount, "category");
      _.forEach(filterValue, value => {
        tabTotalCount += _.get(categoryTotalMap, `${value}.count`, 0);
      });

      const notificationExistOnThisTab = tabTotalCount > 0;

      // check if tab is allowed to be displayed for free org
      const isAllowed = isCurriculumProgramFree ? !paidOnly : true;

      if (
        (_.isEqual(value, "PRIMARY") || notificationExistOnThisTab) &&
        isAllowed &&
        _.includes(userTypes, userType)
      ) {
        categoryTabs.push({
          ...tab,
          label: getTabLabelComponent({
            label: t(label),
            tabCount: tabNewCount,
          }),
          unreadCount: tabNewCount,
        });
      }

      return categoryTabs;
    },
    []
  );

  return categoryTabs;
};

export const getNewNotificationCount = ({
  userId,
  type,
  isCurriculumProgramFree,
  userType,
}) => {
  const notificationCountFromCache = getUserNotificationCountFromCache({
    id: userId,
    type,
    notificationCategoryFilter: getCategoryTabsForNotification({
      isCurriculumProgramFree,
      userType,
    }),
  });
  return _.get(notificationCountFromCache, "notification.newCount", 0);
};
export const getNotificationTypeObj = ({ notificationType }) => {
  return (
    _.get(notificationTypesV2, notificationType) ||
    _.get(notificationTypes, notificationType) ||
    {}
  );
};

export const getSpecifiedIcon = ({
  notificationDetails,
  metadata,
  type,
  entities,
}) => {
  const {
    postType,
    contentType,
    contentSubType,
    primaryResourceType,
    projectGroupType = "",
    projectGroupSubType = "",
    commentSubType = "DEFAULT",
    attachmentType = "DEFAULT",
    messageItemType,
    shareType = "DEFAULT",
    behaviourSubType,
    taskTypeLabel,
    taskTypeColor,
  } = metadata || {};
  const subCategories = _.get(notificationDetails, "subCategories");
  let key;
  if (type === "POST" || type === "STUDENT_POST_PUBLISHED") {
    key = _.size(entities) > 1 ? "POST" : postType;
  } else if (
    type === "ASSIGNMENT_CREATE" ||
    type === "ASSIGNMENT_SHARED_WITH_STUDENT" ||
    type === "STUDENT_ASSIGNMENT_OVERRIDE_STATUS" ||
    type === "STUDENT_ASSIGNMENT_OVERRIDE_RESET_STATUS" ||
    type === "STUDENT_ASSIGNMENT_SUBMISSION" ||
    type === "ALL_STUDENT_ASSIGNMENT_SUBMISSION" ||
    type === "STUDENT_ASSIGNMENT_SHARED" ||
    type === "STUDENT_ASSIGNMENT_SUBMISSION_UNHAND" ||
    type === "ASSIGNMENT_DEADLINE_REMINDER" ||
    type === "PDFTRON_COMMENT_ADDED" ||
    type === "STUDENT_ASSIGNMENT_SUBMISSION_LOCK_UPDATED" ||
    type === "ASSIGNMENT_CLOSE_DATE_UPDATED" ||
    type === "ASSIGNMENT_RESCHEDULED" ||
    type === "ASSIGNMENT_DEADLINE_UPDATED"
  ) {
    if (getIsAssessmentTypeConfigurationEnabled() && taskTypeLabel) {
      return {
        dsIcon: (
          <Avatar
            shape={"circle"}
            shade={"light"}
            size={"medium"}
            name={_.upperCase(taskTypeLabel)}
            dsVersion={"2.0"}
            color={taskTypeColor}
          />
        ),
      };
    }

    if (contentType == "ASSESSMENT" && _.get(subCategories, contentSubType)) {
      key = contentSubType;
    } else {
      key = contentType;
    }

    if (_.isEmpty(key)) {
      return notificationDetails;
    }
  } else if (
    type === "BEHAVIOUR_INCIDENT_UPDATES" ||
    type === "BEHAVIOUR_INCIDENT_ACTIONS" ||
    type === "BEHAVIOUR_INCIDENT_ACCESSIBILITY"
  ) {
    key = behaviourSubType;
  } else if (type === "MULTIPLE_RESOURCE_SHARED_WITH_STUDENT") {
    key = primaryResourceType === "FILE" ? "FILE" : "FOLDER";
  } else if (isIEAssessment({ projectGroupSubType })) {
    key = projectGroupSubType;
  } else if (!_.isEmpty(projectGroupType)) {
    key = projectGroupType;
  } else if (type == "COMMENT_ASSESSMENT" || type == "COMMENT_UNIT_PLAN") {
    key = commentSubType;
  } else if (type == "STUDENT_DRIVE_RESOURCE") {
    key = attachmentType;
    if (!_.has(subCategories, key)) key = "DEFAULT";
    if (_.size(entities) > 1) key = "MULTI_FILES";
  } else if (type == "SCHOOL_POLICIES_AND_RESOURCES") {
    key = _.size(entities) > 1 ? "MULTI_RESOURCES" : "DEFAULT";
  } else if (
    type === "ASSIGNMENT_MESSAGE_SENT" ||
    type === "ASSIGNMENT_GROUP_MESSAGE_SENT"
  ) {
    if (getIsAssessmentTypeConfigurationEnabled() && taskTypeLabel) {
      return {
        dsIcon: (
          <Avatar
            shape={"circle"}
            shade={"light"}
            size={"medium"}
            name={_.upperCase(taskTypeLabel)}
            dsVersion={"2.0"}
            color={taskTypeColor}
          />
        ),
        stackedIcons: true,
        bgColor: taskTypeColor,
      };
    }

    if (_.includes(_.values(ALERT_TYPES), messageItemType)) {
      key = "STUDENT_ASSIGNMENT_ALERT_MESSAGE";
    } else if (_.get(subCategories, contentSubType)) {
      key = contentSubType;
    }
    if (_.isEmpty(key)) {
      return notificationDetails;
    }
  } else if (type === "CLASS_MATERIALS_RESOURCE") {
    // share type is one of ["DEFAULT", "SHARED_SECTION", "SHARED_RESOURCE_INSIDE_SECTION"]
    key = shareType;

    // reflect icon when sharing resources
    if (shareType !== "SHARED_SECTION" && _.size(entities) == 1) {
      const fileType = _.includes(CLASS_MATERIALS_FILE_TYPES, attachmentType)
        ? attachmentType
        : shareType;

      key = fileType;
    }
  } else if (type === "CIRCULAR_ADDED") {
    if (_.get(metadata, "circularCreatedIn") === "CLASS") {
      key = "CLASS_ANNOUNCEMENT";
    } else {
      key = "DEFAULT";
    }
  }
  return _.get(subCategories, key, {});
};

export const getRethumb = ({ imageUrl, imageThumbSize }) => {
  const rethumpUrl = imageUrl
    ? getRethumbUrl({
        width: imageThumbSize,
        height: imageThumbSize,
        imageUrl: imageUrl,
        fitIn: true,
      })
    : "";
  return `url(${rethumpUrl})`;
};

const getUserNotificationCommonCourseIds = ({
  userCourseIds,
  notificationCourseIds,
}) => {
  return _.intersectionWith(
    userCourseIds,
    notificationCourseIds,
    (userCourseId, notificationCourseId) => userCourseId == notificationCourseId
  );
};

export const getUserNotificationCommonCourseIdsMemoize = _.memoize(
  params => getUserNotificationCommonCourseIds(params),
  params => JSON.stringify(params)
);
